import React, { useMemo, useContext } from 'react';
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    useReactTable,
} from '@tanstack/react-table';
import CreateCloudfrontUrl from '/src/services/CreateCloudfrontUrl';
import SkusTable from '/src/Components/SkusTable/SkusTable';
import { AddressSearchContext, WindowSizeContext } from '/src/App';
import './style.css';

const GettingItDoneDIY = ({ project }) => {

    const { deviceType } = useContext(WindowSizeContext);

    // Get the portalData from the AddressSearchContext
    const { portalData } = useContext(AddressSearchContext);

    // Load data from local storage with safety checks
    const project_skus_local_storage = portalData.skus;
    const companies_local_storage = portalData.companies;
    const icons_local_storage = portalData.icons;
    const resources_local_storage = portalData.resources;
    
    // Get the skus from the project with safety checkr
    const project_skus = project.project_skus || [];

    // Get the resources from the project with safety check
    const project_resources = project.project_associated_resources || [];

    // Get the project house category and associated climate risks with safety checks
    const project_home_category = project.project_home_category || '';
    const project_climate_risks_refined = (project.project_associated_climate_risks || [])
        .map(risk => risk?.risk_name)
        .filter(Boolean);

    // Enrich the project_skus with additional information and sort by cost
    const enriched_skus = useMemo(() => 
        project_skus
            .map(sku => {
                // Find matching product from local storage
                const product = project_skus_local_storage.find(
                    local_sku => local_sku.product_id === sku.id
                );

                if (!product) return sku; // Return original if no match found

                // Find company information for sold_by and made_by
                const sold_by_company = companies_local_storage.find(
                    company => company.id === product.sold_by
                );
                
                const made_by_company = companies_local_storage.find(
                    company => company.id === product.made_by
                );

                // Return enriched object
                return {
                    ...sku, // Original id and cost
                    product_name: product.product_name,
                    unit_cost: product.unit_cost,
                    url: product.url,
                    // Sold by information
                    sold_by_name: sold_by_company?.name,
                    sold_by_company_type: sold_by_company?.company_type,
                    sold_by_url: sold_by_company?.company_url,
                    sold_by_icon: CreateCloudfrontUrl(icons_local_storage[sold_by_company?.icon]),
                    // Made by information
                    made_by_name: made_by_company?.name,
                    made_by_company_type: made_by_company?.company_type,
                    made_by_url: made_by_company?.company_url,
                    made_by_icon: CreateCloudfrontUrl(icons_local_storage[made_by_company?.icon])
                };
            })
            .sort((a, b) => a.cost - b.cost)
    , [project_skus, project_skus_local_storage, companies_local_storage, icons_local_storage]);

    console.log('Enriched SKUs:', enriched_skus);

    const getting_it_done_intro = "Let's point you towards the products you're going to need. Below are various options that all fit this project, and remember, if you add this project to your Taking Action hub we may be able to get you additional discounts!";

    const columnHelper = createColumnHelper();

    const columns = useMemo(() => [
        columnHelper.accessor('sold_by_icon', {
            header: 'Where to Find',
            cell: info => (
                <div className="vendor-icon-container">
                    <img 
                        src={info.getValue()} 
                        alt="Vendor logo" 
                        className="vendor-icon"
                    />
                </div>
            ),
        }),
        columnHelper.accessor('product_name', {
            header: 'Product Name',
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('made_by_name', {
            header: 'Made By',
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('cost', {
            header: 'Est Total Cost',
            cell: info => `${Math.round(info.getValue()).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
        }),
        columnHelper.accessor('url', {
            header: 'Go',
            cell: info => (
                <button 
                    className="product-link-button"
                    onClick={() => window.open(info.getValue(), '_blank')}
                >
                    View
                </button>
            ),
        }),
    ], []);

    const table = useReactTable({
        data: enriched_skus,
        columns,
        getCoreRowModel: getCoreRowModel(),
    });

    return (
        <div className={`getting-it-done-diy-container ${deviceType}`}>
            <p className="getting-it-done-intro">{getting_it_done_intro}</p>
            <SkusTable project={project} />
        </div>
    );
};

export default GettingItDoneDIY;